import {omit, keys, spread, merge, map, groupBy, filter, some, reject, assign, omitBy, mapValues, api, isEmpty, Base64, flow} from 'helpers';
import {fork, take, delay, call, put, select} from 'redux-saga/effects';
import {watch, runWhile} from 'store/effects';
import {server, session} from 'store';
import {appSettings} from 'store/settings';

export function* updateManager() {
  yield watch(session.userIsOnline, function* (isOnline) {
    yield runWhile(workers, isOnline);
  });
}

function* workers() {
  yield fork(worker, data);
  yield fork(worker, dataTroublesome);
  yield fork(worker, clarity);
  yield fork(worker, identity);
  yield fork(worker, settings);
}

function* worker({ pattern, uri, selector, reduce, timeout = 1000 }) {
  while (true) {
    const changes = yield select(selector);
    const state = yield select();
    try {
      const [ data, cancelled ] = reduce(changes, state);
      if (!isEmpty(data)) {
        yield put({
          type: 'SERVER_QUERY',
        });
        yield call(api, uri, { data });
      }
      if (!isEmpty(data) || !isEmpty(cancelled)) {
        yield put({
          type: 'SERVER_QUERY_COMPLETED',
          changes,
          cancelled,
        });
      }
    } catch (error) {
      yield put({
        type: 'SERVER_QUERY_COMPLETED',
        changes,
        errorMessage: 'Failed to synchronize',
        error,
        extra: error.request,
      });
    } finally {
      yield take(pattern);
      //wait a little bit to batch multiple changes in one query
      yield delay(timeout);
    }
  }
}

const data = {
  pattern: ['APP_DATA_CHANGED', 'SERVER_QUERY_COMPLETED'],
  uri: '/set/',
  selector: state => {
    return server.unprocessedChanges(state)
                 .filter(({ action, attempts }) => /create|update|delete/.test(action) && attempts < 5)
                 .slice(0, 100);
  },
  reduce: changes => {
    let data = { create: {}, update: {}, delete: {} };

    for (const change of changes) {
      const { action, type, payload } = change;
      data[action][type] = data[action][type] || [];
      //some models may implement toJSON to not waste localStorage memory when serialized
      data[action][type].push(omit(payload, 'toJSON'));
    }

    //if there were subsequent updates to the model that hasn't been synced yet
    for (const type in data.create) {
      for (const create of data.create[type]) {
        const sameId = ({ serverId }) => serverId == create.serverId;
        const updates = filter(data.update[type], sameId);
        data.update[type] = reject(data.update[type], sameId);
        if (updates.length) {
          //merge updates into create
          assign.apply(null, [ create, ...updates ]);
          delete create.lastModifiedDates;
        }
      }
    }

    // remove all create and update queries (unprocessed) if a model was deleted
    for (const type in data.delete) {
      data.delete[type] = filter(data.delete[type], deleted => {
        const sameId = ({ serverId }) => serverId == deleted.serverId;
        const sameCreate = some(data.create[type], sameId);
        data.create[type] = reject(data.create[type], sameId);
        data.update[type] = reject(data.update[type], sameId);
        //keep the delete request if there's no create change pending
        return !sameCreate;
      })
    }

    const cancelled = changes.filter(change => {
      const sameId = ({ serverId }) => serverId == change.payload.serverId;
      return !some(data[change.action][change.type], sameId);
    });

    //reduce updates
    for (const type in data.update) {
      data.update[type] = groupBy(data.update[type], 'serverId');
      data.update[type] = map(data.update[type], spread(merge));
    }

    //remove empty arrays
    data = mapValues(data, changes => omitBy(changes, isEmpty));

    return [ omitBy(data, isEmpty), cancelled ];
  }
}

const dataTroublesome = {
  ...data,
  selector: state => server.unprocessedChanges(state).filter(({ action, attempts }) => /create|update|delete/.test(action) && attempts >= 5),
  timeout: 30*60*1000,
}

const settings = {
  pattern: ['APP_SETTINGS_CHANGED', 'SERVER_QUERY_COMPLETED'],
  uri: '/set-settings/',
  selector: state => server.unprocessedChanges(state).filter(({ action }) => /settings/.test(action)),
  reduce: (changes, state) => {
    const { email } = session.user(state);
    const settings = merge.apply(null, [ { ...state.settings }, ...map(changes, 'payload') ]);
    const data = { ...appSettings({ settings }) };
    data.webSettings = flow(JSON.stringify, Base64.encode)(omit(settings, keys(data), 'today.date')); //do not save today.date
    data.email = email;
    return isEmpty(changes) ? [] : [ data ];
  }
}

const clarity = {
  pattern: ['APP_CLARITY_CHANGED', 'SERVER_QUERY_COMPLETED'],
  uri: '/set-clarity',
  selector: state => server.unprocessedChanges(state).filter(({ action }) => /clarity/.test(action)),
  reduce: changes => changes.slice(0, 1).map(change => change.payload),
}

const identity = {
  pattern: ['APP_IDENTITY_CHANGED', 'SERVER_QUERY_COMPLETED'],
  uri: '/set-identity',
  selector: state => server.unprocessedChanges(state).filter(({ action }) => /identity/.test(action)),
  reduce: changes => changes.slice(0, 1).map(change => change.payload),
}
